import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import update from "immutability-helper";
import { editImages, rotateImage } from "../state/actions/agentActions";
import { useScroll } from "./useScroll";
import { useDragDropManager } from "react-dnd";

export const useImageContainer = ({
  images,
  id,
  _id,
  allSelected,
  allDeselected,
  pageLoading,
  setPageLoading,
}) => {
  const dispatch = useDispatch();
  const isAgent = useSelector(
    (state) =>
      state.account.user?.role === "agent" ||
      state.account.user?.role === "menadzer" ||
      state.account.user?.role === "rukovodilac"
  );

  const [showModal, setShowModal] = useState(null);
  const [cards, setCards] = useState(images);
  const listRef = useRef(null);
  const [rotationVal, setRotationVal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCards(images);
  }, [images, images.length, pageLoading, isAgent]);

  useEffect(() => {
    if (allSelected) {
      setCards((prevCards) =>
        prevCards.map((card) => ({
          ...card,
          approved: true,
        }))
      );
    } else if (allDeselected) {
      setCards((prevCards) =>
        prevCards.map((card) => ({
          ...card,
          approved: false,
        }))
      );
    }
  }, [allSelected, allDeselected]);

  const { updatePosition } = useScroll(listRef);
  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();

  useEffect(() => {
    const unsubscribe = monitor.subscribeToOffsetChange(() => {
      const offset = monitor.getSourceClientOffset()?.y;
      updatePosition({ position: offset, isScrollAllowed: true });
    });
    return unsubscribe;
  }, [monitor, updatePosition]);

  const onSubmit = useCallback(() => {
    const submitCards = cards.map((card, i) => ({
      ...card,
      order_num: i + 1,
    }));
    dispatch(editImages(id, submitCards, id));
  }, [cards, dispatch, id]);

  const rotateLeft = useCallback(() => {
    setLoading(true);
    setRotationVal((prev) => prev - 90);
    setTimeout(() => setLoading(false), 500);
  }, []);

  const rotateRight = useCallback(() => {
    setLoading(true);
    setRotationVal((prev) => prev + 90);
    setTimeout(() => setLoading(false), 500);
  }, []);

  const onSubmitRotate = useCallback(async () => {
    if (showModal) {
      setPageLoading(true);
      try {
        await dispatch(
          rotateImage(showModal.id, _id, { degrees: rotationVal })
        );
        setShowModal(null);
        setRotationVal(0);
      } catch (error) {
        console.error(error);
      }
    }
  }, [showModal, rotationVal, dispatch, _id, setPageLoading]);

  const handleDelete = useCallback((cardId, cardIndex) => {
    setCards((prevCards) =>
      prevCards.filter((card, index) => {
        if (cardId) {
          return card.id !== cardId;
        }
        return index !== cardIndex;
      })
    );
  }, []);

  const handleAdd = useCallback((cardId, cardIndex) => {
    setCards((prevCards) =>
      prevCards.map((card, index) => {
        const isMatch = cardId ? card.id === cardId : index === cardIndex;
        return {
          ...card,
          approved: isMatch ? !card.approved : card.approved,
        };
      })
    );
  }, []);

  const moveCard = useCallback((dragIndex, hoverIndex) => {
    setCards((prevCards) => {
      const dragCard = prevCards[dragIndex];
      return update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragCard],
        ],
      });
    });
  }, []);

  const openModal = useCallback((imageData) => {
    setShowModal(imageData);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(null);
    setRotationVal(0);
  }, []);

  return {
    isAgent,
    cards,
    showModal,
    rotationVal,
    loading,
    listRef,
    onSubmit,
    rotateLeft,
    rotateRight,
    onSubmitRotate,
    handleDelete,
    handleAdd,
    moveCard,
    openModal,
    closeModal,
  };
};
